import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { fetchFinancialsTimeseries } from "../core/axios/endpoints";

const fmtEuro = (n) =>
  `€ ${(n ?? 0).toLocaleString("nl-NL", {
    maximumFractionDigits: 0,
  })}`;

const fmtCount = (n) => (n ?? 0).toLocaleString("nl-NL");

const monthLabels = {
  "01": "jan",
  "02": "feb",
  "03": "mrt",
  "04": "apr",
  "05": "mei",
  "06": "jun",
  "07": "jul",
  "08": "aug",
  "09": "sep",
  10: "okt",
  11: "nov",
  12: "dec",
};

const monthLabel = (ym) => {
  const [, m] = ym.split("-");
  return monthLabels[m] ?? ym;
};

const RANGE_OPTIONS = [
  { value: "6m", label: "6 mnd" },
  { value: "12m", label: "12 mnd" },
  { value: "24m", label: "24 mnd" },
];

const Card = ({ title, subtitle, action, children, padded = true }) => (
  <div className="rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
    <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
        ) : null}
      </div>
      {action ?? null}
    </div>
    <div className={padded ? "p-4" : ""}>{children}</div>
  </div>
);

const FinancialCharts = () => {
  const [range, setRange] = useState("12m");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFinancialsTimeseries(range)
      .then((res) => {
        if (cancelled) return;
        setData(res?.data ?? res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error ?? err?.message ?? "Kon data niet laden");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  const revenueData = useMemo(
    () =>
      (data?.revenue ?? []).map((r) => ({
        ...r,
        label: monthLabel(r.month),
      })),
    [data],
  );

  const volumeData = useMemo(
    () =>
      (data?.volume ?? []).map((v) => ({
        ...v,
        label: monthLabel(v.month),
      })),
    [data],
  );

  const totalRevenue = useMemo(
    () => revenueData.reduce((s, r) => s + (r.total ?? 0), 0),
    [revenueData],
  );

  const totalVolume = useMemo(
    () =>
      volumeData.reduce(
        (s, v) =>
          s + (v.eia ?? 0) + (v.eiaZon ?? 0) + (v.isde ?? 0) + (v.btwCustomer ?? 0) + (v.btwPartner ?? 0),
        0,
      ),
    [volumeData],
  );

  const funnel = data?.funnel ?? { total: 0, qualified: 0, paid: 0 };
  const aging = data?.aging ?? { notYetDue: 0, d0_14: 0, d15_30: 0, d30plus: 0 };

  const fmtSel = (
    <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
      {RANGE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => setRange(opt.value)}
          className={`rounded px-2 py-1 text-xs font-medium transition ${
            range === opt.value
              ? "bg-gray-900 text-white"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        Charts niet geladen: {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Financiële inzichten
          </h2>
          <p className="text-xs text-gray-500">
            {loading ? "Laden…" : `Periode: laatste ${range.replace("m", " maanden")}`}
          </p>
        </div>
        {fmtSel}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Totale omzet (periode)" subtitle="Subsidie + BTW + partner">
          <p className="text-2xl font-semibold tabular-nums text-emerald-700">
            {fmtEuro(totalRevenue)}
          </p>
        </Card>
        <Card title="Aanvragen (periode)" subtitle="Subsidie + BTW">
          <p className="text-2xl font-semibold tabular-nums text-gray-900">
            {fmtCount(totalVolume)}
          </p>
        </Card>
        <Card title="BTW funnel (alltime)" subtitle="ingestuurd → geschikt → betaald">
          <div className="flex items-end gap-1 text-xs tabular-nums">
            <span className="text-gray-900">
              <span className="text-lg font-semibold">{fmtCount(funnel.total)}</span> totaal
            </span>
            <span className="text-gray-400">›</span>
            <span className="text-emerald-700">
              <span className="font-semibold">{fmtCount(funnel.qualified)}</span> geschikt
            </span>
            <span className="text-gray-400">›</span>
            <span className="text-emerald-700">
              <span className="font-semibold">{fmtCount(funnel.paid)}</span> betaald
            </span>
          </div>
        </Card>
      </div>

      <Card
        title="Omzet per maand"
        subtitle="Subsidie-facturen, BTW klant en partner-facturen gestapeld"
      >
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revenueData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-subsidie" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad-btw" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad-partner" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(value, name) => [
                  fmtEuro(value),
                  name === "subsidie"
                    ? "Subsidie-facturen"
                    : name === "btwCustomer"
                    ? "BTW klant"
                    : name === "partner"
                    ? "Partner-facturen"
                    : name,
                ]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area
                type="monotone"
                dataKey="subsidie"
                stackId="1"
                stroke="#10b981"
                fill="url(#grad-subsidie)"
                name="Subsidie"
              />
              <Area
                type="monotone"
                dataKey="btwCustomer"
                stackId="1"
                stroke="#0ea5e9"
                fill="url(#grad-btw)"
                name="BTW klant"
              />
              <Area
                type="monotone"
                dataKey="partner"
                stackId="1"
                stroke="#8b5cf6"
                fill="url(#grad-partner)"
                name="Partner"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card
        title="Aanvragen-volume per maand"
        subtitle="Per regeling gestapeld"
      >
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={volumeData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="eia" stackId="a" fill="#f59e0b" name="EIA" />
              <Bar dataKey="eiaZon" stackId="a" fill="#fbbf24" name="EIA Zon" />
              <Bar dataKey="isde" stackId="a" fill="#fb923c" name="ISDE" />
              <Bar dataKey="btwCustomer" stackId="a" fill="#0ea5e9" name="BTW klant" />
              <Bar dataKey="btwPartner" stackId="a" fill="#8b5cf6" name="BTW partner" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Partner-facturen aging" subtitle="Open op factuur (verstuurd of overdue)">
          <ul className="space-y-2 text-sm">
            {[
              { label: "Nog niet vervallen", value: aging.notYetDue, tone: "bg-slate-400" },
              { label: "0 - 14 dagen", value: aging.d0_14, tone: "bg-emerald-500" },
              { label: "15 - 30 dagen", value: aging.d15_30, tone: "bg-amber-500" },
              { label: "30+ dagen", value: aging.d30plus, tone: "bg-rose-500" },
            ].map((b) => {
              const max =
                Math.max(
                  aging.notYetDue,
                  aging.d0_14,
                  aging.d15_30,
                  aging.d30plus,
                  1,
                );
              const pct = Math.max(2, Math.round((b.value / max) * 100));
              return (
                <li key={b.label}>
                  <div className="mb-1 flex items-baseline justify-between text-xs">
                    <span className="text-gray-600">{b.label}</span>
                    <span className="font-medium tabular-nums text-gray-900">
                      {fmtEuro(b.value)}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div className={`h-full ${b.tone}`} style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card title="BTW kwalificatie-mix" subtitle="Verdeling van alle BTW aanvragen">
          <ul className="space-y-2 text-sm">
            {[
              { label: "Geschikt", value: funnel.qualified, tone: "bg-emerald-500" },
              { label: "Bel klant", value: funnel.callRequired, tone: "bg-amber-500" },
              { label: "Handmatige beoordeling", value: funnel.disqualified, tone: "bg-amber-500" },
            ].map((b) => {
              const pct =
                funnel.total > 0
                  ? Math.max(2, Math.round((b.value / funnel.total) * 100))
                  : 0;
              return (
                <li key={b.label}>
                  <div className="mb-1 flex items-baseline justify-between text-xs">
                    <span className="text-gray-600">{b.label}</span>
                    <span className="font-medium tabular-nums text-gray-900">
                      {fmtCount(b.value)} ({pct}%)
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div className={`h-full ${b.tone}`} style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default FinancialCharts;
